import { markRaw } from "vue";

import Avatar from "~/components/dumb/Avatar.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import StatusIcon from "~/components/dumb/StatusIcon.vue";
import { COMPLETED_STATUS_KINDS } from "~/components/visualization/constants";
import { DEFAULT_CHIP_COLOR } from "~/constants/style";
import { AttachmentFieldIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import { IconSize, PageKind, TypeaheadSpecialEntity, ViewKind } from "~/shared/enums";
import type { Page, TypeaheadGetOptionsFn, TypeaheadGetOptionsResult } from "~/shared/types";
import { isString } from "~/shared/typeUtils";
import { useDataStore, useUserStore } from "~/stores";
import { getPageDisplayName } from "~/utils/common";
import { getUserDisplayName } from "~/utils/user";

const ONE_MINUTE_IN_MS = 60 * 1000;

type TypeaheadSection = {
  getOptions: TypeaheadGetOptionsFn;
  title: string;
  type: TypeaheadSpecialEntity.TASK | TypeaheadSpecialEntity.DOC;
};

const typeaheadResultCache = new Map<string, { promise: Promise<TypeaheadGetOptionsResult>; timestamp: number }>();

const createCachedGetter = (cacheKeyPrefix: string) => (query: string, optionsFn: TypeaheadGetOptionsFn) => {
  const cacheKey = `${cacheKeyPrefix}:${query}`;
  const cached = typeaheadResultCache.get(cacheKey);
  const now = Date.now();

  if (cached && now - cached.timestamp < ONE_MINUTE_IN_MS) {
    return cached.promise;
  }

  const promise = (async () => {
    try {
      return await optionsFn(query);
    } catch (error) {
      typeaheadResultCache.delete(cacheKey);
      throw error;
    }
  })();

  typeaheadResultCache.set(cacheKey, { promise, timestamp: now });
  return promise;
};

const getTaskOptionsUncached: TypeaheadGetOptionsFn = async (query, requestKey) => {
  const dataStore = useDataStore();

  const load = async (offset: number) => {
    const queryNorm = query.toString();
    const results = await dataStore.loadTasks(
      { title: TypeaheadSpecialEntity.TASK.includes(queryNorm) ? "" : query, inTrash: false, isDraft: false },
      offset,
      { key: requestKey ? `typeahead-tasks-${requestKey}` : "typeahead-tasks", limit: 50 }
    );

    return {
      options: results.data.results.map((task) => {
        const statusKind = dataStore.getStatusByDuid(task.statusDuid)?.kind;
        const dartboard = dataStore.getDartboardByDuid(task.dartboardDuid);
        const pageName = getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "full");
        const spaceAbrev = getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "abrev");
        return {
          value: task.duid,
          label: task.title,
          specialEntity: TypeaheadSpecialEntity.TASK,
          adtlSearchTerms: [TypeaheadSpecialEntity.TASK, pageName, spaceAbrev],
          icon: markRaw(StatusIcon),
          iconArgs: {
            class: "icon-md",
            duid: task.statusDuid,
          },
          chipColorHex: DEFAULT_CHIP_COLOR,
          strikethrough: statusKind && COMPLETED_STATUS_KINDS.has(statusKind),
        };
      }),
      total: results.data.count,
    };
  };

  const initialTasks = await load(0);
  return { key: "tasks", ...initialTasks, loadMore: load };
};

const getDocOptionsUncached: TypeaheadGetOptionsFn = async (query, requestKey) => {
  const dataStore = useDataStore();

  const load = async (offset: number) => {
    const queryNorm = query.toString();
    const results = await dataStore.loadDocs(
      { title: TypeaheadSpecialEntity.DOC.includes(queryNorm) ? "" : query, order: "title" },
      offset,
      { key: requestKey ? `typeahead-docs-${requestKey}` : "typeahead-docs", limit: 50 }
    );

    return {
      options: results.data.results.map((doc) => {
        const folder = dataStore.getFolderByDuid(doc.folderDuid);
        const pageName = getPageDisplayName(folder, dataStore.getSpaceByDuid, "full");
        const spaceAbrev = getPageDisplayName(folder, dataStore.getSpaceByDuid, "abrev");

        return {
          value: doc.duid,
          label: doc.title,
          specialEntity: TypeaheadSpecialEntity.DOC,
          adtlSearchTerms: [TypeaheadSpecialEntity.DOC, pageName, spaceAbrev],
          icon: markRaw(PageIcon),
          iconArgs: {
            page: doc,
          },
          chipColorHex: DEFAULT_CHIP_COLOR,
          chipIcon: markRaw(PageIcon),
          chipIconArgs: {
            page: doc,
            size: IconSize.S,
          },
        };
      }),
      total: results.data.count,
    };
  };

  const initialDocs = await load(0);
  return { key: "docs", ...initialDocs, loadMore: load };
};

export const getTaskOptions: TypeaheadGetOptionsFn = (query, requestKey) =>
  createCachedGetter("task")(query, (e) => getTaskOptionsUncached(e, requestKey));

export const getDocOptions: TypeaheadGetOptionsFn = (query, requestKey) =>
  createCachedGetter("doc")(query, (e) => getDocOptionsUncached(e, requestKey));

export const getSkillOptionsSync = () => {
  const options = useDataStore()
    .skillList()
    .map((skill) => ({
      value: skill.duid,
      label: skill.title,
      specialEntity: TypeaheadSpecialEntity.SKILL,
      adtlSearchTerms: ["skill"],
      icon: PageIcon,
      iconArgs: {
        page: skill,
      },
      chipColorHex: DEFAULT_CHIP_COLOR,
      chipIcon: markRaw(PageIcon),
      chipIconArgs: {
        page: skill,
        size: IconSize.S,
      },
    }));

  return { key: "skills", options, total: options.length };
};

export const getUserOptionsSync = () => {
  const dataStore = useDataStore();
  const options = dataStore.getUserList({ excludeAgents: true }).map((user) => ({
    value: user.duid,
    label: getUserDisplayName(user),
    specialEntity: TypeaheadSpecialEntity.USER,
    property: dataStore.defaultAssigneesProperty,
    adtlSearchTerms: ["user", user.email],
    icon: Avatar,
    iconArgs: {
      user,
      class: "icon-md",
    },
  }));

  return { key: "teammates", options, total: options.length };
};
export const ADD_ATTACHMENT_OPTION_VALUE = "add-attachment";
const getAttachmentInputOptionsSync = () => ({
  key: "attachments",
  options: [
    {
      value: ADD_ATTACHMENT_OPTION_VALUE,
      label: "Add attachment",
      icon: AttachmentFieldIcon,
      chipColorHex: DEFAULT_CHIP_COLOR,
      chipIcon: markRaw(AttachmentFieldIcon),
      chipIconArgs: {
        size: IconSize.S,
      },
    },
  ],
  total: 1,
});

export const getViewOptionsSync = () => {
  const options = useDataStore()
    .viewList.filter((view) => view.kind === ViewKind.CUSTOM)
    .map((view) => ({
      value: view.duid,
      label: view.title,
      specialEntity: TypeaheadSpecialEntity.VIEW,
      adtlSearchTerms: ["view"],
      icon: PageIcon,
      iconArgs: {
        page: view,
      },
      chipColorHex: DEFAULT_CHIP_COLOR,
      chipIcon: markRaw(PageIcon),
      chipIconArgs: {
        page: view,
        size: IconSize.S,
      },
    }));

  return { key: "views", options, total: options.length };
};

export const getDartboardOptionsSync = () => {
  const dataStore = useDataStore();

  const options = dataStore.getDartboardList().map((dartboard) => ({
    value: dartboard.duid,
    label: getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "abrev"),
    specialEntity: TypeaheadSpecialEntity.DARTBOARD,
    adtlSearchTerms: ["dartboard", dartboard.title],
    icon: PageIcon,
    iconArgs: {
      page: dartboard,
    },
    chipColorHex: DEFAULT_CHIP_COLOR,
    chipIcon: markRaw(PageIcon),
    chipIconArgs: {
      page: dartboard,
      size: IconSize.S,
    },
  }));

  return { key: "dartboards", options, total: options.length };
};

export const getFolderOptionsSync = () => {
  const dataStore = useDataStore();

  const options = dataStore.getFolderList().map((folder) => ({
    value: folder.duid,
    label: getPageDisplayName(folder, dataStore.getSpaceByDuid, "abrev"),
    specialEntity: TypeaheadSpecialEntity.FOLDER,
    adtlSearchTerms: ["folder"],
    icon: PageIcon,
    iconArgs: {
      page: folder,
    },
    chipColorHex: DEFAULT_CHIP_COLOR,
    chipIcon: markRaw(PageIcon),
    chipIconArgs: {
      page: folder,
      size: IconSize.S,
    },
  }));

  return { key: "folders", options, total: options.length };
};

const MINOR_ENTITY_OPTION_FNS_SYNC = [getDartboardOptionsSync, getViewOptionsSync, getFolderOptionsSync];
const MINOR_ENTITY_OPTION_FNS: TypeaheadGetOptionsFn[] = MINOR_ENTITY_OPTION_FNS_SYNC.map(
  (getOptions) => async () => getOptions()
);

export const getCurrentContextTitle = (page: Page): string => {
  const dataStore = useDataStore();
  if (page.pageKind !== PageKind.DARTBOARD && page.pageKind !== PageKind.FOLDER) {
    return getPageDisplayName(page, dataStore.getSpaceByDuid, "abrev");
  }

  const entity =
    page.pageKind === PageKind.DARTBOARD
      ? dataStore.getDartboardByDuid(page.duid)
      : dataStore.getFolderByDuid(page.duid);

  return entity
    ? getPageDisplayName(entity, dataStore.getSpaceByDuid, "abrev")
    : getPageDisplayName(page, dataStore.getSpaceByDuid, "abrev");
};

const filterOptionsFromResults = (
  dataStore: ReturnType<typeof useDataStore>,
  result: TypeaheadGetOptionsResult,
  type: TypeaheadSpecialEntity.TASK | TypeaheadSpecialEntity.DOC,
  duid?: string,
  exclude = false
) => {
  if (!duid) {
    return result;
  }
  const options = result.options.filter((e) => {
    if (!isString(e.value)) {
      return false;
    }
    const item =
      type === TypeaheadSpecialEntity.TASK
        ? dataStore.getTaskByDuidIfLoaded(e.value)?.dartboardDuid
        : dataStore.getDocByDuidIfLoaded(e.value)?.folderDuid;
    return exclude ? item !== duid : item === duid;
  });

  return { ...result, options, total: options.length };
};

const createSharedOptionsGetters = (
  dataStore: ReturnType<typeof useDataStore>,
  optionsFn: TypeaheadGetOptionsFn,
  type: TypeaheadSpecialEntity.TASK | TypeaheadSpecialEntity.DOC,
  currentDuid: string
) => {
  const getCachedResult = createCachedGetter(type);
  return {
    currentPage: async (query: string) => {
      const result = await getCachedResult(query, optionsFn);
      return filterOptionsFromResults(dataStore, result, type, currentDuid, false);
    },
    otherPages: async (query: string) => {
      const result = await getCachedResult(query, optionsFn);
      return filterOptionsFromResults(dataStore, result, type, currentDuid, true);
    },
    getCachedResult: (query: string) => getCachedResult(query, optionsFn),
  };
};

export const createTypeaheadSections = (currentPage?: Page): TypeaheadSection[] => {
  const dataStore = useDataStore();
  const userStore = useUserStore();

  const getRecent = (
    type: TypeaheadSpecialEntity.TASK | TypeaheadSpecialEntity.DOC,
    getCachedResult: (query: string) => Promise<TypeaheadGetOptionsResult>
  ) => {
    const stack = type === TypeaheadSpecialEntity.TASK ? userStore.recentTaskStack : userStore.recentDocStack;
    const getItem =
      type === TypeaheadSpecialEntity.TASK ? dataStore.getTaskByDuidIfLoaded : dataStore.getDocByDuidIfLoaded;

    return async (query: string) => {
      const items = stack
        .map((duid: string) => getItem(duid))
        .filter(isDefined)
        .filter((item) => item.title.includes(query));

      const baseOptions = await getCachedResult("");
      const options = items.map((item) => baseOptions.options.find((e) => e.value === item?.duid)).filter(isDefined);
      return { key: `recent-${type}s`, options, total: options.length };
    };
  };

  const sharedTaskGetters = createSharedOptionsGetters(
    dataStore,
    getTaskOptionsUncached,
    TypeaheadSpecialEntity.TASK,
    currentPage?.duid ?? ""
  );
  const sharedDocGetters = createSharedOptionsGetters(
    dataStore,
    getDocOptionsUncached,
    TypeaheadSpecialEntity.DOC,
    currentPage?.duid ?? ""
  );

  if (currentPage?.pageKind === PageKind.DARTBOARD) {
    return [
      {
        getOptions: getRecent(TypeaheadSpecialEntity.TASK, sharedTaskGetters.getCachedResult),
        title: "Recent tasks",
        type: TypeaheadSpecialEntity.TASK,
      },
      {
        getOptions: sharedTaskGetters.currentPage,
        title: getCurrentContextTitle(currentPage),
        type: TypeaheadSpecialEntity.TASK,
      },
      {
        getOptions: sharedTaskGetters.otherPages,
        title: "Other tasks",
        type: TypeaheadSpecialEntity.TASK,
      },
      {
        getOptions: getRecent(TypeaheadSpecialEntity.DOC, sharedDocGetters.getCachedResult),
        title: "Recent docs",
        type: TypeaheadSpecialEntity.DOC,
      },
      { getOptions: getDocOptions, title: "All docs", type: TypeaheadSpecialEntity.DOC },
    ];
  }

  if (currentPage?.pageKind === PageKind.FOLDER) {
    return [
      {
        getOptions: getRecent(TypeaheadSpecialEntity.DOC, sharedDocGetters.getCachedResult),
        title: "Recent docs",
        type: TypeaheadSpecialEntity.DOC,
      },
      {
        getOptions: sharedDocGetters.currentPage,
        title: getCurrentContextTitle(currentPage),
        type: TypeaheadSpecialEntity.DOC,
      },
      {
        getOptions: sharedDocGetters.otherPages,
        title: "Other docs",
        type: TypeaheadSpecialEntity.DOC,
      },
      {
        getOptions: getRecent(TypeaheadSpecialEntity.TASK, sharedTaskGetters.getCachedResult),
        title: "Recent tasks",
        type: TypeaheadSpecialEntity.TASK,
      },
      { getOptions: getTaskOptions, title: "All tasks", type: TypeaheadSpecialEntity.TASK },
    ];
  }

  return [
    {
      getOptions: getRecent(TypeaheadSpecialEntity.TASK, sharedTaskGetters.getCachedResult),
      title: "Recent tasks",
      type: TypeaheadSpecialEntity.TASK,
    },
    {
      getOptions: getRecent(TypeaheadSpecialEntity.DOC, sharedDocGetters.getCachedResult),
      title: "Recent docs",
      type: TypeaheadSpecialEntity.DOC,
    },
    { getOptions: getTaskOptions, title: "All tasks", type: TypeaheadSpecialEntity.TASK },
    { getOptions: getDocOptions, title: "All docs", type: TypeaheadSpecialEntity.DOC },
  ];
};

export const createChatContextOptionFns = (
  currentPage?: Page,
  options: { includeSkills?: boolean; includeUsers?: boolean; hideAttachmentOption?: boolean } = {}
): TypeaheadGetOptionsFn[] => [
  ...(options.includeUsers ? [async () => getUserOptionsSync()] : []),
  ...(options.includeSkills ? [async () => getSkillOptionsSync()] : []),
  ...(options.hideAttachmentOption ? [] : [async () => getAttachmentInputOptionsSync()]),
  ...createTypeaheadSections(currentPage).map(({ getOptions, title }) => async (query: string) => ({
    ...(await getOptions(query)),
    title,
  })),
  ...MINOR_ENTITY_OPTION_FNS,
];
